import { esc, fmtDate, fmtMonth, nf1, num } from '../calc/format.js';
import { AppError, normalize, readFile } from '../calc/load.js';
import { RANGE_PRESETS, daysBetween, rangePreset, shiftYear } from '../calc/periods.js';
import { NONE, exactGroup, groupLabel, groupPaths, supplierValues } from '../calc/selection.js';
import { storeLabels, supplierLabel, supplierNames } from '../calc/model.js';
import { destroyChart, renderDailyChart, renderHourCharts, renderWeekdayChart } from './charts.js';
import { el } from './dom.js';
import { placeViewButton, setupViewButtons, syncFullscreenLabel, toggleFullscreen } from './fullscreen.js';
import { closeHelp, initHelp } from './help.js';
import { initCollapse } from './collapse.js';
import { closeQuickFilter, initQuickFilter } from './quickfilter.js';
import { initSidebar } from './sidebar.js';
import { closeCalendar, initCalendar, initDateField, setDateBounds } from './datepicker.js';
import { exportMargin, exportProducts } from './export.js';
import { applyFilter, rebuildSeries } from './filters.js';
import { renderCompare, renderInsights } from './insights.js';
import { renderKpis } from './kpis.js';
import { MultiSelect } from './multiselect.js';
import { clockOptions, clockValue } from '../calc/timefilter.js';
import { restoreFromHash, watchHash, writeHash } from './urlstate.js';
import { renderReceipts } from './receipts.js';
import { state } from './state.js';
import { renderCashiers, renderLowMargin, renderMargin, renderPayments, renderCards, renderProducts, renderReturns, renderSplit, renderStores, renderTopProfit, renderWeekdayTable } from './tables.js';
import { exportTrend, renderTrend } from './trend.js';
import { renderHeat } from './heatmap.js';
import { renderBasket, selectBasketItem } from './basket.js';
import { customerNames, customerOptions } from '../calc/customers.js';
import { exportCustomers, renderCustomers } from './customers.js';
import { exportDiscounts, renderPromos } from './promos.js';

export function renderAll(){
  closeHelp();   // en öppen förklaring hör till knappar som nu ritas om
  closeCalendar();
  const s = state.stats;
  const hasData = state.view.length > 0;

  renderKpis(s, hasData);
  renderDailyChart(s, hasData);
  renderWeekdayChart(s, hasData);
  renderSplit(s, hasData);
  renderHourCharts(s, hasData);
  renderHeat(s, hasData);
  renderWeekdayTable(s, hasData);
  renderBasket(hasData);
  renderProducts();
  renderPromos();
  renderMargin();
  renderStores(s, hasData);
  renderCashiers(s, hasData);
  renderPayments(s, hasData);
  renderCards(s, hasData);
  renderCustomers();
  renderInsights(s, hasData);
  renderCompare(s, hasData);
  renderReceipts(s, hasData);
  renderTrend();
  renderTopProfit(s, hasData);
  renderLowMargin(s, hasData);
  renderReturns(s, hasData);
}

/* --------------------------------------------------------------------------
   Uppstart och händelser
   -------------------------------------------------------------------------- */

export function showError(err){
  const box = el('uploadError');
  const title  = err instanceof AppError ? err.title  : 'Filen kunde inte läsas';
  const detail = err instanceof AppError ? err.detail : 'Försök igen, eller gör en ny kvittoexport från Back office.';
  box.innerHTML = `<strong>${esc(title)}</strong>${esc(detail)}`;
  box.style.display = 'block';
  if(!(err instanceof AppError)) console.error(err);
}

export function clearError(){ el('uploadError').style.display = 'none'; }

/** Läsbar filstorlek. */
export function fileSize(bytes){
  if(bytes >= 1048576) return nf1.format(bytes/1048576).replace('.',',') + ' MB';
  if(bytes >= 1024)    return Math.round(bytes/1024) + ' kB';
  return bytes + ' byte';
}

/**
 * Väntar tills webbläsaren hunnit rita. Inläsningen av filen blockerar
 * huvudtråden, så utan den här pausen hinner statusmeddelandet aldrig synas —
 * skärmen står bara still tills allt är klart.
 */
export function nextPaint(){
  return new Promise(resolve =>
    requestAnimationFrame(() => requestAnimationFrame(() => setTimeout(resolve, 0))));
}

export function showLoading(file){
  const big = file.size >= 8 * 1048576;
  el('dropzone').hidden = true;
  el('loading').hidden = false;
  el('loadingFile').textContent = `${file.name} · ${fileSize(file.size)}`;
  el('loadingHint').textContent = big
    ? 'Filen innehåller många kvitton, så det kan ta en stund. Hur lång tid beror på hur snabb datorn är.'
    : 'Det brukar gå på ett ögonblick.';
}

export function hideLoading(){
  el('loading').hidden = true;
  el('dropzone').hidden = false;
}

const STAGES = {
  las:     'Läser in filen …',
  tolka:   'Går igenom alla kvitton …',
  analys:  'Räknar fram nyckeltalen …'
};
function showStage(steg){ el('loadingStage').textContent = STAGES[steg] || STAGES.las; }

/**
 * Läser och normaliserar filen i en Web Worker, så att sidan inte fryser.
 * Ger null om ingen worker går att starta — då läser handleFile på sidans
 * egen tråd i stället. Kastar AppError precis som readFile och normalize.
 */
function parseInWorker(file){
  if(typeof Worker !== 'function') return Promise.resolve(null);
  return new Promise((resolve, reject) => {
    let worker;
    try{ worker = new Worker('worker.js'); }
    catch{ resolve(null); return; }
    const receipts = [];
    worker.onmessage = e => {
      const m = e.data;
      if(m.type === 'steg'){ showStage(m.steg); return; }
      // Kvittona kommer i bitar, så att sidan kan rita mellan dem
      if(m.type === 'del'){ for(const r of m.receipts) receipts.push(r); return; }
      worker.terminate();
      if(m.type === 'klar' && receipts.length === m.antal) resolve(receipts);
      else if(m.type === 'klar') reject(new Error(`Fick ${receipts.length} av ${m.antal} kvitton från inläsningen`));
      else reject(m.appError ? new AppError(m.title, m.detail) : new Error(m.message));
    };
    // Skriptet gick inte att ladda eller kraschade utan svar: läs på sidan i stället
    worker.onerror = e => { e.preventDefault(); worker.terminate(); resolve(null); };
    worker.postMessage({file});
  });
}

/** Ökas för varje ny fil. Ett svar från en äldre inläsning kastas. */
let job = 0;

export async function handleFile(file){
  if(!file) return;
  const mine = ++job;
  clearError();
  showLoading(file);
  showStage('las');
  await nextPaint();          // låt statusmeddelandet ritas innan bearbetningen
  try{
    let receipts = await parseInWorker(file);
    let where = 'worker';
    if(receipts === null){
      where = 'main';
      receipts = normalize(await readFile(file));
    }
    if(mine !== job) return;  // kunden har hunnit välja en annan fil
    showStage('analys');
    await nextPaint();
    state.receipts = receipts;
    document.body.dataset.inlasning = where;   // för testerna: var tolkades filen
    startAnalysis();
  }catch(err){
    if(mine === job) showError(err);
  }finally{
    if(mine === job) hideLoading();
  }
}

export function startAnalysis(){
  const rs = state.receipts;
  const first = rs[0].day;
  const last  = rs[rs.length-1].day;
  const months = [...new Set(rs.map(r => r.month))].sort();
  state.bounds = {min:first, max:last, months};

  // Period-panel
  el('pRange').textContent = first === last ? fmtDate(first) : `${fmtDate(first)} – ${fmtDate(last)}`;
  const stores = new Set(rs.map(r => r.store));
  el('pMeta').innerHTML =
    `<b>${esc(num(rs.length))}</b> kvitton · <b>${esc(num(months.length))}</b> ${months.length === 1 ? 'månad' : 'månader'}` +
    // Antalet butiker säger något först när de är fler än en
    (stores.size > 1 ? ` · <b>${esc(num(stores.size))}</b> butiker` : '') +
    // Exporten har UTC; kunden ska veta att klockslagen redan är omräknade
    `<span class="tz">Tiderna är i svensk tid, med sommar- och vintertid</span>`;

  // Datumfält: begränsa till filens spann
  setDateBounds([el('fDate'), el('fFrom'), el('fTo')], first, last);
  el('fDate').value  = last;
  el('fFrom').value  = first;
  el('fTo').value    = last;

  // Månad: filens månader, senaste först. Förvald är senaste månaden MED
  // försäljning, inte innevarande kalendermånad.
  const mIn = el('fMonth');
  mIn.innerHTML = [...months].reverse()
    .map(m => `<option value="${esc(m)}">${esc(fmtMonth(m))}</option>`).join('');
  mIn.value = months[months.length-1];

  // Snabbval som filen inte räcker till, till exempel hela veckor i en fil
  // på tre dagar, visas men går inte att välja
  el('fPreset').innerHTML = '<option value="">Snabbval …</option>' + RANGE_PRESETS
    .map(p => `<option value="${esc(p.key)}"${rangePreset(p.key, state.bounds) ? '' : ' disabled'}>${esc(p.label)}</option>`)
    .join('');

  el('tMonth').checked = true;
  syncTimeInputs();

  // Upplösning i utvecklingsvyn efter hur lång filen är. En fil på några
  // veckor har ingen nytta av en månadsvy med en enda stapel.
  const spanDays = daysBetween(first, last);
  state.grain = spanDays > 120 ? 'month' : spanDays > 21 ? 'week' : 'day';
  el('trendGrain').value = state.grain;

  // Går det att jämföra med förra året? Annars ska alternativet inte locka.
  const yearBack = shiftYear(last, -1);
  const hasYearAgo = first <= yearBack;
  const yoyOpts = [el('compareMode'), el('compareModeAlt')];
  yoyOpts.forEach(sel => {
    const opt = [...sel.options].find(o => o.value === 'yoy');
    opt.disabled = !hasYearAgo;
    opt.textContent = hasYearAgo
      ? 'Samma period förra året'
      : 'Samma period förra året (saknas i filen)';
  });
  state.compareMode = 'prev';
  yoyOpts.forEach(sel => sel.value = 'prev');

  // Urvalslistor. Ett flerval med ett enda värde stängs av av sig självt.
  const numbers = key => [...new Set(rs.map(r => r[key]))].filter(v => v != null).sort((a,b)=>a-b);
  // Butiker visas bara när filen har fler än en, med namn eller gatuadress
  state.storeLabels = storeLabels(rs);
  state.multiStore = numbers('store').length > 1;
  el('fStore').closest('.scope-row').hidden = !state.multiStore;
  state.scopeSelects.store.setOptions(numbers('store').map(v =>
    ({value:String(v), label: state.storeLabels.get(v) || 'Butik ' + v})));
  state.scopeSelects.register.setOptions(numbers('register').map(v => ({value:String(v), label:'Kassa ' + v})));
  state.scopeSelects.cashier.setOptions([...new Set(rs.map(r => r.cashier))]
    .sort((a,b)=>a.localeCompare(b,'sv')).map(v => ({value:v, label:v})));
  // Kunder i lojalitetsprogrammet. Urvalet och fliken Kunder visas bara när
  // filen har kvitton med kund; "Utan kund" står sist, som "Saknar artikelgrupp".
  state.customerNames = customerNames(rs);
  state.hasCustomers = state.customerNames.size > 0;
  state.hasBonus = rs.some(r => r.bonusCount > 0);
  el('customerRow').hidden = !state.hasCustomers;
  el('railKund').hidden = !state.hasCustomers;
  state.scopeSelects.customer.setOptions(customerOptions(rs));
  state.customerQuery = '';
  el('qCustomer').value = '';
  // Kampanjer och rabatt på artikel: fliken visas när filen har något av dem
  state.hasPromos = rs.some(r => r.lines.some(l => l.promoName != null));
  state.hasDiscounts = rs.some(r => r.lines.some(l => l.promoName == null && l.discount));
  showTab('pCustomers', state.hasCustomers);
  showTab('pPromos', state.hasPromos || state.hasDiscounts);

  // Värdet NONE står sist, avskilt, och väljer rader utan grupp eller
  // leverantör — så hittar kunden artiklar att rätta i artikelregistret
  state.lineSelects.group.setOptions(groupPaths(rs).map(v => ({
    value: v, label: groupLabel(v === NONE ? '' : v), special: v === NONE,
    short: exactGroup(v) !== null ? 'Utan undergrupp' : undefined})), {tree:true});
  const names = supplierNames(rs);
  state.lineSelects.supplier.setOptions(supplierValues(rs).map(v => ({
    value: v, label: supplierLabel(v === NONE ? '' : v, names.get(v)), special: v === NONE})));

  // Har någon såld artikel i filen inköpspris? Annars visas varken
  // bruttovinst eller marginal, och måtten går inte att välja i utvecklingen.
  state.hasCost = rs.some(r => r.lines.some(l => l.q > 0 && !l.noCost));
  // Måtten om kunder och bonuscheckar går bara att välja när filen har dem
  const usable = {profit: state.hasCost, margin: state.hasCost,
                  custShare: state.hasCustomers, custRevenueShare: state.hasCustomers,
                  custAov: state.hasCustomers, bonusCount: state.hasBonus};
  for(const o of el('trendMetric').options){
    if(o.value in usable) o.disabled = !usable[o.value];
  }
  if(usable[state.metric] === false){
    state.metric = 'revenue';
    el('trendMetric').value = 'revenue';
  }

  // Kortterminalens kvitton: finns de, och vilka markeringar har de
  const cards = rs.flatMap(r => r.cards).filter(c => c.terminal);
  state.terminal = {any: cards.length > 0,
                    contactless: cards.some(c => c.contactless), pin: cards.some(c => c.pin)};

  el('uploadView').hidden = true;
  el('analysisView').hidden = false;
  placeViewButton(true);
  document.body.classList.add('has-analysis');   // sidhuvud och sidfot i full bredd, som menyn
  // Sidfoten står i innehållets kolumn, så att sidomenyns kolumn går ända
  // ned. Annars knuffar sidfoten upp menyn längst ned på sidan.
  el('mainCol').append(document.querySelector('footer'));

  // Ett urval som sparats i adressen, om det passar den här filen. En trasig
  // adress får aldrig stoppa analysen: då gäller standardurvalet.
  let tab = null;
  try{ tab = restoreFromHash(); }catch{ tab = null; }
  applyFilter();
  if(tab) selectTab(tab);
}

/** Tonar ned fälten för de tidsalternativ som inte är valda. */
export function syncTimeInputs(){
  document.querySelectorAll('input[name=tf]').forEach(r =>
    r.closest('.timeopt').classList.toggle('is-inactive', !r.checked));
}

/* Varje fält hör till ett tidsalternativ. Att klicka i fältet eller ändra
   det väljer alternativet, så att kunden inte först måste klicka på
   radioknappen. Bara fokus räcker inte: den som tabbar förbi fälten ska inte
   byta period av misstag. */
const TIME_FIELDS = {fDate:'tDate', fMonth:'tMonth', fFrom:'tRange', fTo:'tRange'};

/** Väljer tidsalternativet. Sant om det inte redan var valt. */
function activateTimeMode(radioId){
  if(el(radioId).checked) return false;
  el(radioId).checked = true;
  syncTimeInputs();
  return true;
}

/** Klockslagen i halvtimmar. Det tomma värdet, 00:00, är ingen gräns:
    dygnets början i "från", dygnets slut i "till". */
function fillClock(sel){
  sel.innerHTML = '<option value="">00:00</option>' +
    clockOptions().map(c => `<option value="${c}">${c}</option>`).join('');
}

/** Sätter ett klockslag. Ett klockslag utanför halvtimmarna, från en äldre
    adress, läggs till i listan på sin plats i stället för att tappas. */
export function setClock(id, v){
  const sel = el(id), value = clockValue(v);
  if(value && ![...sel.options].some(o => o.value === value)){
    const opt = new Option(value, value);
    sel.add(opt, [...sel.options].find(o => o.value > value) || null);
  }
  sel.value = value;
}

/** Visar eller döljer en flik. Är den dold och vald går visningen till Översikt. */
function showTab(panel, visible){
  const tab = document.querySelector(`.tab[data-panel="${panel}"]`);
  tab.hidden = !visible;
  if(!visible && tab.getAttribute('aria-selected') === 'true') selectTab(document.querySelector('.tab'));
}

export function resetFilters(){
  el('tMonth').checked = true;
  el('fMonth').value = state.bounds.months[state.bounds.months.length-1];
  el('fDate').value  = state.bounds.max;
  el('fFrom').value  = state.bounds.min;
  el('fTo').value    = state.bounds.max;
  document.querySelectorAll('input.wd').forEach(c => c.checked = false);
  setClock('fClockFrom', ''); setClock('fClockTo', '');
  Object.values(state.scopeSelects).forEach(ms => ms.clear());
  Object.values(state.lineSelects).forEach(ms => ms.clear());
  el('qProduct').value = '';
  state.productQuery = '';
  syncTimeInputs();
  applyFilter();
}

export function initEvents(){
  initHelp();
  initCollapse();
  initSidebar();
  initQuickFilter();
  initCalendar();
  state.scopeSelects.store = new MultiSelect(el('fStore'),
    {label:'Butik', allLabel:'Alla butiker', plural:'butiker'});
  state.scopeSelects.register = new MultiSelect(el('fRegister'),
    {label:'Kassa', allLabel:'Alla kassor', plural:'kassor'});
  state.scopeSelects.cashier = new MultiSelect(el('fCashier'),
    {label:'Kassör', allLabel:'Alla kassörer', plural:'kassörer'});
  state.scopeSelects.customer = new MultiSelect(el('fCustomer'),
    {label:'Kund', allLabel:'Alla kunder', plural:'kunder'});
  state.lineSelects.group = new MultiSelect(el('fGroup'),
    {label:'Artikelgrupp', allLabel:'Alla artikelgrupper', plural:'artikelgrupper'});
  state.lineSelects.supplier = new MultiSelect(el('fSupplier'),
    {label:'Leverantör', allLabel:'Alla leverantörer', plural:'leverantörer'});

  // Uppladdning
  el('fileInput').addEventListener('change', e => {
    handleFile(e.target.files[0]);
    e.target.value = '';   // samma fil ska kunna väljas igen
  });

  const dz = el('dropzone');
  ['dragenter','dragover'].forEach(ev => dz.addEventListener(ev, e => {
    e.preventDefault(); dz.classList.add('over');
  }));
  ['dragleave','drop'].forEach(ev => dz.addEventListener(ev, e => {
    e.preventDefault(); dz.classList.remove('over');
  }));
  dz.addEventListener('drop', e => {
    const f = e.dataTransfer?.files?.[0];
    if(f) handleFile(f);
  });

  // Tidsfilter
  document.querySelectorAll('input[name=tf]').forEach(r =>
    r.addEventListener('change', () => { syncTimeInputs(); applyFilter(); }));
  for(const [id, radioId] of Object.entries(TIME_FIELDS)){
    // Datumfälten får sin kalenderknapp och tolkningen av det kunden skriver
    // först, så att lyssnarna nedan alltid får ett giltigt datum
    const targets = [el(id)];
    if(el(id).classList.contains('date-input')) targets.push(initDateField(el(id)));
    // Klick: alternativet gäller direkt, med det värde fältet redan har.
    // Väljer kunden sedan samma datum som står där blir det ändå rätt.
    for(const t of targets) t.addEventListener('pointerdown', () => { if(activateTimeMode(radioId)) applyFilter(); });
    el(id).addEventListener('change', () => { activateTimeMode(radioId); applyFilter(); });
  }
  el('fPreset').addEventListener('change', e => {
    const r = e.target.value && rangePreset(e.target.value, state.bounds);
    if(!r) return;
    el('fFrom').value = r.from;
    el('fTo').value = r.to;
    activateTimeMode('tRange');
    applyFilter();
  });

  // Veckodagar och klockslag. Klockslagen är listor, inte type=time: ett
  // tidsfält visar AM/PM när datorns språk är engelska, och det går inte att
  // styra från sidan. Listan visar 24-timmarstid i varje webbläsare.
  document.querySelectorAll('input.wd').forEach(c => c.addEventListener('change', applyFilter));
  for(const id of ['fClockFrom','fClockTo']){
    fillClock(el(id));
    el(id).addEventListener('change', applyFilter);
  }
  el('btnDayTimeClear').addEventListener('click', () => {
    document.querySelectorAll('input.wd').forEach(c => c.checked = false);
    setClock('fClockFrom', ''); setClock('fClockTo', '');
    applyFilter();
    el('wd0').focus();   // knappen döljs; fokus ska inte försvinna med den
  });

  ['fStore','fRegister','fCashier','fCustomer','fGroup','fSupplier'].forEach(id =>
    el(id).addEventListener('change', applyFilter));

  el('btnReset').addEventListener('click', resetFilters);
  watchHash(applyFilter);

  // Helskärm
  setupViewButtons(false);
  el('btnFullscreen').addEventListener('click', toggleFullscreen);
  ['fullscreenchange','webkitfullscreenchange'].forEach(ev =>
    document.addEventListener(ev, () => {
      syncFullscreenLabel();
      // Chart.js behöver mäta om sig när ytan ändras
      Object.values(state.charts).forEach(c => c && c.resize());
    }));
  el('btnNewFile').addEventListener('click', () => {
    closeQuickFilter();
    Object.keys(state.charts).forEach(destroyChart);
    state.receipts = []; state.view = []; state.stats = null;
    el('analysisView').hidden = true;
    el('uploadView').hidden = false;
    placeViewButton(false);
    document.body.classList.remove('has-analysis');
    document.querySelector('main').after(document.querySelector('footer'));
    clearError();
    window.scrollTo({top:0});
  });

  // Flikar
  document.querySelectorAll('.tab').forEach(tab => {
    tab.addEventListener('click', () => selectTab(tab));
    tab.addEventListener('keydown', e => {
      const tabs = [...document.querySelectorAll('.tab:not([hidden])')];
      const i = tabs.indexOf(tab);
      if(e.key === 'ArrowRight'){ e.preventDefault(); tabs[(i+1) % tabs.length].focus(); }
      if(e.key === 'ArrowLeft'){  e.preventDefault(); tabs[(i-1+tabs.length) % tabs.length].focus(); }
    });
  });

  // Sortering
  el('tProducts').addEventListener('click', e => handleSort(e, state.productSort, renderProducts));
  el('tProducts').addEventListener('keydown', e => {
    if(e.key === 'Enter' || e.key === ' '){ e.preventDefault(); handleSort(e, state.productSort, renderProducts); }
  });
  el('tMargin').addEventListener('click', e => handleSort(e, state.marginSort, renderMargin));
  el('tMargin').addEventListener('keydown', e => {
    if(e.key === 'Enter' || e.key === ' '){ e.preventDefault(); handleSort(e, state.marginSort, renderMargin); }
  });

  for(const [id, sort, render] of [['tCustomers', state.customerSort, renderCustomers],
                                   ['tDisc', state.discSort, renderPromos]]){
    el(id).addEventListener('click', e => handleSort(e, sort, render));
    el(id).addEventListener('keydown', e => {
      if(e.target.closest('th.sortable') && (e.key === 'Enter' || e.key === ' ')){ e.preventDefault(); handleSort(e, sort, render); }
    });
  }

  // Kunder: "Visa" väljer kunden i urvalet, så att varje flik bara visar
  // den kundens köp. Fokus går till kundprofilen som då visas.
  el('tCustomers').addEventListener('click', e => {
    const b = e.target.closest('button[data-customer]');
    if(!b) return;
    state.scopeSelects.customer.value = [b.dataset.customer];
    applyFilter();
    el('profileTitle').focus();
  });
  el('btnAllCustomers').addEventListener('click', () => {
    state.scopeSelects.customer.clear();
    applyFilter();
    el('qCustomer').focus();   // knappen försvinner med profilen
  });
  let ct;
  el('qCustomer').addEventListener('input', e => {
    clearTimeout(ct);
    const v = e.target.value;
    ct = setTimeout(() => { state.customerQuery = v; renderCustomers(); }, 160);
  });
  el('btnExportCustomers').addEventListener('click', exportCustomers);
  el('btnExportDisc').addEventListener('click', exportDiscounts);

  el('mGroup').addEventListener('change', renderMargin);
  el('btnMoreKpis').addEventListener('click', e => {
    const open = e.currentTarget.getAttribute('aria-expanded') !== 'true';
    e.currentTarget.setAttribute('aria-expanded', String(open));
    e.currentTarget.querySelector('span').textContent = open ? 'Färre nyckeltal' : 'Fler nyckeltal';
    el('moreKpis').hidden = !open;
  });
  el('heatMetric').addEventListener('change', () => renderHeat(state.stats, state.view.length > 0));
  el('basketItem').addEventListener('change', e => selectBasketItem(e.target.value));
  el('basketPairs').addEventListener('click', e => {
    const b = e.target.closest('button[data-key]');
    if(b) selectBasketItem(b.dataset.key, true);
  });

  // Utvecklingsvyn: måttet och tabellen räknas om lokalt, upplösningen
  // kräver att serien byggs på nytt
  el('trendMetric').addEventListener('change', e => {
    state.metric = e.target.value;
    renderTrend();
  });
  el('trendGrain').addEventListener('change', e => {
    state.grain = e.target.value;
    rebuildSeries();
    renderTrend();
  });
  el('trendSpan').addEventListener('change', e => {
    state.trendSpan = e.target.value;
    rebuildSeries();
    renderTrend();
    writeHash();
  });
  el('btnExportTrend').addEventListener('click', exportTrend);

  // Jämförelsegrund. Två väljare speglar varandra så att den även går att
  // ändra från tomtillståndet.
  ['compareMode','compareModeAlt'].forEach(id => {
    el(id).addEventListener('change', e => {
      state.compareMode = e.target.value;
      el('compareMode').value = state.compareMode;
      el('compareModeAlt').value = state.compareMode;
      applyFilter();
    });
  });

  // Sök
  let t;
  el('qProduct').addEventListener('input', e => {
    clearTimeout(t);
    const v = e.target.value;
    t = setTimeout(() => { state.productQuery = v; renderProducts(); }, 160);
  });

  el('btnExport').addEventListener('click', exportProducts);
  el('btnExportMargin').addEventListener('click', exportMargin);
}

export function handleSort(e, sort, rerender){
  const th = e.target.closest('th.sortable');
  if(!th) return;
  const key = th.dataset.key;
  if(sort.key === key){
    sort.dir = sort.dir === 'desc' ? 'asc' : 'desc';
  }else{
    sort.key = key;
    sort.dir = key === 'name' ? 'asc' : 'desc';
  }
  rerender();
}

export function selectTab(tab){
  document.querySelectorAll('.tab').forEach(t => t.setAttribute('aria-selected','false'));
  document.querySelectorAll('.panel').forEach(p => p.classList.remove('active'));
  tab.setAttribute('aria-selected','true');
  el(tab.dataset.panel).classList.add('active');
  // Flikraden står kvar överst när man rullat ner. Byter man flik där ska
  // den nya fliken börja direkt under raden, inte mitt i — utan animation,
  // flikbyten görs ofta
  const bar = el('tabbar');
  if(getComputedStyle(bar).position === 'sticky' && bar.getBoundingClientRect().top <= 1){
    const panel = el(tab.dataset.panel);
    window.scrollTo(0, window.scrollY + panel.getBoundingClientRect().top - bar.offsetHeight - 16);
  }
  // På smal skärm rullar flikraden i sidled; den valda fliken ska synas
  tab.scrollIntoView?.({block: 'nearest', inline: 'nearest'});
  // Chart.js behöver mäta om sig när en dold canvas blir synlig
  Object.values(state.charts).forEach(c => c && c.resize());
  writeHash();
}

/* Testsviterna styr några lägen som annars kräver en särskild webbläsare
   eller en stor fil: helskärm som inte stöds och inläsningsvyn. Bara de
   funktionerna exponeras, och de rör inga kvittodata. */
window.__ecTest = {setupViewButtons, showLoading, hideLoading};

initEvents();
